/* eslint max-lines: ["error", {"max": 3000, "skipComments": true, "skipBlankLines": true }] */
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorIcon from '@mui/icons-material/Error';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Unstable_Grid2 as Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  TextField as MuiTextField,
  Paper,
  Radio,
  Step,
  StepButton,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import axios from 'axios';
import { casingFormat } from 'common/utils';
import { differenceInYears, format, parse } from 'date-fns';
import {
  capitalize,
  compact,
  debounce,
  find,
  first,
  forEach,
  includes,
  isArray,
  isEmpty,
  isNil,
  keys,
  map,
  omit,
  reduce,
  slice,
  some,
  sortBy,
  throttle,
  toLower,
  uniq,
  values,
} from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { Field, Form, useForm, useFormState } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import { listCentralFillReasonsApi } from 'api/centralFill';
import {
  createPOMOTCOrderApi,
  createPomOrderApi,
  getRxProofUploadUrlsApi,
  listShippingOptionsApi,
} from 'api/order';
import { getPartnerCatalogApi } from 'api/partner';
import { getPatientsApi, validateAddressApi } from 'api/patient';
import { listPracticeProvidersApi } from 'api/practice';
import { listSigsApi } from 'api/prescription';
import gender from 'common/constants/gender';
import usStateAbbreviation from 'common/constants/usStateAbbreviation';
import CheckboxField from 'common/forms/CheckboxField';
import DateField from 'common/forms/DateField';
import {
  composeValidations,
  validateCombinedAddressLength,
  validateGreaterThan,
  validateNPI,
  validatePhoneNumber,
  validateRequired,
  validateZipCode,
} from 'common/forms/formValidations';
import SelectField from 'common/forms/SelectField';
import TextField from 'common/forms/TextField';
import useInfoDialog from 'common/hooks/useInfoDialog';
import useRoles from 'common/hooks/useRoles';
import VerifiedTag from 'common/modules/VerifiedTag';
import { validateASCIIChars } from 'common/utils/asciiValidation';
import getRejectionReasonLabel from 'common/utils/rejectionReasonLabel';
import { ProviderVerificationStatus } from 'enums/common.enum';
import { VerificationStatus } from 'enums/provider.enum';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { getPartnerPractices } from 'store/thunks/partnerThunks';
import usePartnerContactForPatient from 'modules/OrderView/hooks/usePartnerContactForPatient';
import PartnerContactValue from 'modules/PatientView/components/PartnerContacts/PartnerContactValue';

import prescriptionDetails from 'common/constants/prescriptionDetails';
import RadioGroupField from 'common/forms/RadioGroupField';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import useNewOrderForm from '../hooks/useNewOrderForm';
import {
  orderSubmissionMessage,
  orderSubmissionTone,
  routedLineNotes,
} from '../utils/routingOutcomeMessage';
import useOtcItemsList, { OTCItemsListProvider } from '../hooks/useOtcItemsList';
import CreateNewPatientButton from './CreateNewPatientButton';
import buildPrescriptionOrderInput, {
  NO_PRODUCTS_AVAILABLE_MESSAGE,
  hasNoOrderableCatalog,
} from './createOrder.helper';

const FormStateUpdater = () => {
  const formState = useFormState();
  const { currentStep, updateFormState } = useNewOrderForm();

  useEffect(() => {
    updateFormState({ step: currentStep, valid: !formState.invalid });
  }, [formState.invalid, currentStep, updateFormState]);

  return null;
};

const SourceForm = () => {
  const form = useForm();
  const dispatch = useDispatch();
  const formState = useFormState();
  const { enqueueSnackbar } = useSnackbar();
  const [hasShownSnackbar, setHasShownSnackbar] = useState(false);
  const partners = useSelector(({ partner, auth }) => partner[auth.currentTenantId]);
  const practices = useSelector(({ partner, auth }) => {
    const partnerRecord = find(partner[auth.currentTenantId], {
      partnerUuid: formState.values.partnerId,
    });

    return partnerRecord?.partnerPractices;
  });
  const selectedPartner = find(partners, { partnerUuid: formState.values.partnerId });

  useEffect(() => {
    if (formState.values.partnerId) {
      dispatch(getPartnerPractices({ partnerIds: [formState.values.partnerId] }));
      getPartnerCatalogApi({ partnerId: formState.values.partnerId, onlyPartnerEnabled: true })
        .then((partnerFormulary) => form.change('partnerFormulary', partnerFormulary))
        .catch((error) => dispatch(handleError({ error })));
    }
  }, [dispatch, form, formState.values.partnerId]);

  useEffect(() => {
    listCentralFillReasonsApi()
      .then((compoundingReasons) => form.change('compoundingReasons', compoundingReasons))
      .catch((error) => dispatch(handleError({ error })));
  }, [dispatch, form]);

  useEffect(() => {
    setHasShownSnackbar(false);
  }, [formState.values.partnerId]);

  useEffect(() => {
    if (
      formState.values.partnerId &&
      selectedPartner &&
      !selectedPartner.enabled &&
      !hasShownSnackbar
    ) {
      enqueueSnackbar(
        'Order submission is restricted. The selected partner is disabled in the system.',
        {
          variant: 'error',
        }
      );
      setHasShownSnackbar(true);
    }
  }, [formState.values.partnerId, selectedPartner, enqueueSnackbar, hasShownSnackbar]);

  useEffect(() => {
    if (
      formState.values.practiceId &&
      !find(practices, { practiceId: formState.values.practiceId })
    ) {
      form.change('practiceId', undefined);
    }

    if (
      formState.values.practiceId &&
      find(practices, { practiceId: formState.values.practiceId })
    ) {
      listShippingOptionsApi({
        partnerUUID: formState.values.partnerId,
        practiceId: formState.values.practiceId,
      })
        .then((shippingOptions) => {
          form.batch(() => {
            form.change('shippingOptions', shippingOptions);
            form.change('shippingOption', find(shippingOptions, { isDefault: true })?.optionCode);
          });
        })
        .catch((error) => dispatch(handleError({ error })));

      listPracticeProvidersApi({ practiceId: formState.values.practiceId, limit: 0, offset: 0 })
        .then(({ result }) =>
          form.change(
            'practiceProviders',
            // API order otherwise; the provider picker offers them A–Z by the name it shows.
            [...(result || [])].sort((left, right) =>
              `${left?.firstName ?? ''} ${left?.lastName ?? ''}`.localeCompare(
                `${right?.firstName ?? ''} ${right?.lastName ?? ''}`,
                undefined,
                { sensitivity: 'base' }
              )
            )
          )
        )
        .catch((error) => dispatch(handleError({ error })));

      form.change('practiceDetails', find(practices, { practiceId: formState.values.practiceId }));
    }
  }, [formState.values.practiceId, form, practices, formState.values.partnerId, dispatch]);

  useEffect(() => {
    const partner = find(partners, { partnerUuid: formState.values.partnerId });

    if (
      !isNil(partner?.partnerAccount) &&
      !find(partner.partnerAccount, { partnerAccountId: formState.values.partnerAccountId })
    ) {
      form.change('partnerAccountId', undefined);
    }
  }, [form, formState.values.partnerAccountId, formState.values.partnerId, partners]);

  return (
    <Grid container rowSpacing={2}>
      <Grid xs={12}>
        <Typography component='h2' variant='h6' color='primary'>
          Source
        </Typography>
      </Grid>

      <Grid xs={12}>
        <Box sx={{ width: 400 }}>
          <SelectField
            id='NewOrder-SourceForm-partnerId-dropdown'
            name='partnerId'
            label='Partner'
            required
            endAdornment={
              isNil(partners) ? <CircularProgress sx={{ mr: 3 }} size={20} /> : undefined
            }
          >
            {map(partners, (partner) => (
              <MenuItem
                key={partner.partnerUuid}
                value={partner.partnerUuid}
                disabled={partner.isSuspended}
              >
                {partner.isSuspended ? (
                  <Tooltip title='This partner is suspended.' placement='right' arrow>
                    <Box
                      component='span'
                      onClick={(event) => event.stopPropagation()}
                      sx={{ width: '100%', pointerEvents: 'auto' }}
                    >
                      {casingFormat(partner.partnerName)}
                    </Box>
                  </Tooltip>
                ) : (
                  casingFormat(partner.partnerName)
                )}
              </MenuItem>
            ))}
          </SelectField>
        </Box>
      </Grid>

      <Grid xs={12}>
        <Box sx={{ width: 400 }}>
          <SelectField
            id='NewOrder-SourceForm-practiceId-dropdown'
            name='practiceId'
            label='Practice'
            required
            disabled={!formState.values.partnerId || isNil(practices)}
            endAdornment={
              formState.values.partnerId && isNil(practices) ? (
                <CircularProgress sx={{ mr: 3 }} size={20} />
              ) : undefined
            }
          >
            {map(practices, (practice) => (
              <MenuItem key={practice.practiceId} value={practice.practiceId}>
                {casingFormat(practice.practiceName)}
              </MenuItem>
            ))}
          </SelectField>
        </Box>

        <Grid xs={12}>
          <Box sx={{ width: 400 }}>
            <SelectField
              id='NewOrder-SourceForm-partnerAccountId-dropdown'
              name='partnerAccountId'
              label='Partner Account'
              disabled={!formState.values.partnerId}
            >
              {map(selectedPartner?.partnerAccount, (partnerAccount) => (
                <MenuItem
                  key={partnerAccount.partnerAccountId}
                  value={partnerAccount.partnerAccountId}
                >
                  {capitalize(partnerAccount.partnerAccountName)}
                </MenuItem>
              ))}
              {isEmpty(selectedPartner?.partnerAccount) && (
                <MenuItem disabled>No Accounts Found</MenuItem>
              )}
            </SelectField>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

// The searched patient's email and phone as the order's partner has them (POM-6400).
const PatientContactRows = ({ mpi, partnerUuid }) => {
  const partnerContact = usePartnerContactForPatient({ mpi, partnerUuid });
  const labelSx = { color: (theme) => lighten(theme.palette.text.primary, 0.3) };

  return (
    <>
      <Grid xs={3} sx={labelSx}>
        Email
      </Grid>
      <Grid xs={9}>
        <PartnerContactValue partnerContact={partnerContact} contactKind='email' />
      </Grid>

      <Grid xs={3} sx={labelSx}>
        Phone Number
      </Grid>
      <Grid xs={9}>
        <PartnerContactValue partnerContact={partnerContact} contactKind='phone' />
      </Grid>
    </>
  );
};

PatientContactRows.propTypes = {
  mpi: PropTypes.string.isRequired,
  partnerUuid: PropTypes.string,
};

PatientContactRows.defaultProps = {
  partnerUuid: undefined,
};

const PatientForm = () => {
  const form = useForm();
  const selectedPatientId = useFormState().values.patientInput?.patientId;
  const { patientData, practiceId, partnerId } = useFormState().values;
  const [patients, setPatients] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef('');

  const handleOnChange = (event) => {
    setInputValue(event.target.value);
    inputRef.current = event.target.value;
  };

  const handlePatientSearch = useMemo(
    () =>
      debounce(({ patientName }) => {
        getPatientsApi({
          filter: { name: patientName, practiceId },
          sortBy: { sortKey: 'firstName', sortOrder: 'ASC' },
          page: 1,
        })
          .then(({ patients: results }) => {
            if (inputRef.current === patientName) {
              setPatients(results);
            }
          })
          .finally(() => setLoading(false));
      }, 500),
    [practiceId]
  );

  const selectPatient =
    ({ patient }) =>
    () => {
      form.change('patientInput.patientId', patient.mpi);
      form.change('patientInput.externalPatientId', patient.mpi);
      form.change('patientData', patient);
      form.change(
        'patientAddress',
        find(patient.addresses, ({ tags }) => includes(tags, 'DEFAULT'))?.address
      );
    };

  const onCreatePatient = ({ patient }) => {
    setInputValue(`${casingFormat(patient.firstName)} ${casingFormat(patient.lastName)}`);
    inputRef.current = `${casingFormat(patient.firstName)} ${casingFormat(patient.lastName)}`;
  };

  useEffect(() => {
    if (inputValue) {
      setLoading(true);
      setPatients(null);
      handlePatientSearch({ patientName: inputValue });
    }
  }, [inputValue, handlePatientSearch]);

  useEffect(() => {
    if (inputValue === '' && selectedPatientId && patientData) {
      setPatients([patientData]);
    }
  }, [selectedPatientId, patientData, inputValue]);

  return (
    <Grid container rowSpacing={3} sx={{ maxWidth: 1000, mt: 1 }}>
      <Typography component='h2' variant='h6' color='primary'>
        Patient
      </Typography>

      <Box sx={visuallyHidden} tabIndex={-1}>
        <TextField
          name='patientInput.patientId'
          id='textField'
          label='Patient Id'
          disabled
          required
        />
      </Box>

      <Grid xs={12}>
        <Typography>
          Please start by searching patient name. If patient was not found, feel free to create new
          patient profile.
        </Typography>
      </Grid>

      <Grid xs={12}>
        <Box sx={{ width: 1000 }}>
          <MuiTextField
            fullWidth
            label='Search By Patient Name'
            onChange={handleOnChange}
            value={inputValue}
            InputProps={{
              endAdornment: loading ? <CircularProgress color='inherit' size={20} /> : undefined,
            }}
          />
        </Box>
      </Grid>

      <Grid xs={12}>
        <div>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row-reverse',
              width: 1000,
            }}
          >
            <CreateNewPatientButton onCreatePatient={onCreatePatient} />
            {isArray(patients) && !patients.length && (
              <Typography sx={{ color: (theme) => theme.palette.info.main }}>
                No patients found with this name. Please create a new patient profile.
              </Typography>
            )}
          </Box>
        </div>
      </Grid>

      {isArray(patients) && patients.length > 0 && (
        <Grid xs={12}>
          <Typography color='primary' variant='h6' component='p'>
            Please select a patient.
          </Typography>
          <Box>
            {map(patients, (patient) => (
              <Accordion
                key={patient.mpi}
                expanded={patients.length === 1 ? true : undefined}
                // Details mount when opened, so a patient's partner contacts load only when viewed.
                TransitionProps={{ unmountOnExit: true }}
              >
                <AccordionSummary
                  expandIcon={patients.length === 1 ? undefined : <ExpandMoreIcon />}
                >
                  <Typography sx={{ minWidth: 450 }}>
                    {patient.firstName} {patient.lastName}
                  </Typography>
                  <Typography sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}>
                    {format(parse(patient.dob, 'yyyy-MM-dd', new Date()), 'yyyy-MM-dd')} (
                    {differenceInYears(new Date(), parse(patient.dob, 'yyyy-MM-dd', new Date()))}{' '}
                    yo)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container>
                    <Grid xs={6}>
                      <Box>
                        <Grid container rowSpacing={1}>
                          <Grid
                            xs={3}
                            sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}
                          >
                            First Name
                          </Grid>
                          <Grid xs={9}>{patient.firstName}</Grid>

                          <Grid
                            xs={3}
                            sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}
                          >
                            Last Name
                          </Grid>
                          <Grid xs={9}>{patient.lastName}</Grid>

                          <Grid
                            xs={3}
                            sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}
                          >
                            Date of Birth
                          </Grid>
                          <Grid xs={9}>
                            {format(parse(patient.dob, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy')}
                          </Grid>

                          <Grid
                            xs={3}
                            sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}
                          >
                            Gender
                          </Grid>
                          <Grid xs={9}>{gender[patient.gender]}</Grid>
                        </Grid>
                      </Box>
                    </Grid>

                    <Grid xs={6}>
                      <Box>
                        <Grid container>
                          <PatientContactRows mpi={patient.mpi} partnerUuid={partnerId} />

                          <Grid
                            xs={3}
                            sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}
                          >
                            Address
                          </Grid>
                          <Grid xs={9}>
                            <div>
                              {
                                find(patient.addresses, ({ tags }) => includes(tags, 'DEFAULT'))
                                  ?.address.line1
                              }
                              ,
                            </div>
                            <div>
                              {
                                find(patient.addresses, ({ tags }) => includes(tags, 'DEFAULT'))
                                  ?.address.line2
                              }
                            </div>

                            <div>
                              {
                                find(patient.addresses, ({ tags }) => includes(tags, 'DEFAULT'))
                                  ?.address.city
                              }
                              ,&nbsp;
                              {
                                find(patient.addresses, ({ tags }) => includes(tags, 'DEFAULT'))
                                  ?.address.state
                              }
                              &nbsp;
                              {
                                find(patient.addresses, ({ tags }) => includes(tags, 'DEFAULT'))
                                  ?.address.zip
                              }
                            </div>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>

                <AccordionActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant='contained'
                    disabled={selectedPatientId === patient.mpi}
                    sx={{ width: 200 }}
                    onClick={selectPatient({ patient })}
                  >
                    {selectedPatientId === patient.mpi ? 'Selected' : 'Select Patient'}
                  </Button>
                </AccordionActions>
              </Accordion>
            ))}
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

const PrescriptionRecordFileUploader = ({ prescriptionIndex }) => {
  const form = useForm();
  const { InfoDialog, showInfoDialog } = useInfoDialog();

  const handleDrop = (files) => {
    if (
      files.length +
        values(form.getState().values.prescriptionInput[prescriptionIndex].attachments || {})
          .length >
      5
    ) {
      showInfoDialog({
        title: 'Max files Exceeded',
        message: 'Only 5 files are allowed per prescription',
      });
      return;
    }
    const filesObject = reduce(
      files,
      (results, file) => ({
        ...results,
        [uuidV4()]: {
          file,
          uploadStatus: 'pending',
          filename: file.name,
          contentType: file.type,
          uploadUrl: undefined,
          removeUrl: undefined,
          key: undefined,
        },
      }),
      {}
    );

    form.change(`prescriptionInput[${prescriptionIndex}].attachments`, {
      ...(form.getState().values.prescriptionInput[prescriptionIndex].attachments || {}),
      ...filesObject,
    });
  };

  const handleSizeError = (error) => {
    showInfoDialog({
      title: 'Upload Error',
      message: `${error}. Max File Size: 2mb, Supported File Type: 'png', 'jpg, 'jpeg', 'pdf'`,
    });
  };

  return (
    <Box
      sx={{
        '& label': { width: '100%', maxWidth: '100vw !important', height: 96 },
        span: { fontSize: '1.3rem !important' },
        '& .file-types': { display: 'none' },
      }}
    >
      <InfoDialog />
      <Field
        name={`prescriptionInput.[${prescriptionIndex}].attachments`}
        validate={(value) => {
          if (isEmpty(value)) {
            return 'Required';
          }

          if (some(value, (attachment) => attachment.uploadStatus !== 'uploaded')) {
            return 'Pending upload';
          }

          return undefined;
        }}
        render={() => (
          <FileUploader
            handleChange={handleDrop}
            name={`${prescriptionIndex}-uploadFile-input`}
            types={['png', 'jpeg', 'jpg', 'pdf']}
            label='Upload Prescription Record(s) (png, jpg, jpeg, pdf)'
            uploadedLabel='Upload Prescription Record(s) (png, jpg, jpeg, pdf)'
            required
            multiple
            maxSize={2}
            onSizeError={handleSizeError}
          />
        )}
      />
    </Box>
  );
};

PrescriptionRecordFileUploader.propTypes = {
  prescriptionIndex: PropTypes.number.isRequired,
};

const PrescriptionsFileList = ({ prescriptionIndex }) => {
  const form = useForm();
  const dispatch = useDispatch();
  const formState = useFormState();

  const handleRemoveAttachment = (e) => {
    const attachmentId = e.currentTarget?.dataset?.attachmentId;

    axios
      .delete(
        formState.values.prescriptionInput[prescriptionIndex].attachments[attachmentId].removeUrl
      )
      .then(() =>
        form.change(
          `prescriptionInput[${prescriptionIndex}].attachments`,
          omit(
            form.getState().values.prescriptionInput[prescriptionIndex].attachments,
            attachmentId
          )
        )
      )
      .catch((error) => dispatch(handleError({ error })));
  };

  useEffect(() => {
    form.batch(() => {
      forEach(
        formState.values.prescriptionInput[prescriptionIndex].attachments,
        (attachment, attachmentId) => {
          if (attachment.uploadStatus === 'pending') {
            form.change(
              `prescriptionInput[${prescriptionIndex}].attachments[${attachmentId}].uploadStatus`,
              'uploading'
            );
            getRxProofUploadUrlsApi({
              filename: attachment.filename,
              contentType: attachment.contentType,
            })
              .then((data) => {
                const fileData = first(data);
                form.batch(() => {
                  form.change(
                    `prescriptionInput[${prescriptionIndex}].attachments[${attachmentId}].uploadUrl`,
                    fileData.uploadUrl
                  );
                  form.change(
                    `prescriptionInput[${prescriptionIndex}].attachments[${attachmentId}].removeUrl`,
                    fileData.removeUrl
                  );
                  form.change(
                    `prescriptionInput[${prescriptionIndex}].attachments[${attachmentId}].key`,
                    fileData.key
                  );
                });

                return axios.put(fileData.uploadUrl, attachment.file).then(() => {
                  form.change(
                    `prescriptionInput[${prescriptionIndex}].attachments[${attachmentId}].uploadStatus`,
                    'uploaded'
                  );
                });
              })
              .catch((error) => {
                dispatch(handleError({ error }));
                form.change(
                  `prescriptionInput[${prescriptionIndex}].attachments`,
                  omit(
                    form.getState().values.prescriptionInput[prescriptionIndex].attachments,
                    attachmentId
                  )
                );
              });
          }
        }
      );
    });
  }, [form, formState.values.prescriptionInput, prescriptionIndex, dispatch]);

  return (
    <Box>
      <List>
        {map(
          formState.values.prescriptionInput[prescriptionIndex].attachments,
          (attachment, attachmentId) => (
            <ListItem
              key={attachmentId}
              disableGutters
              secondaryAction={
                attachment.uploadStatus === 'uploading' ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton
                    data-attachment-id={attachmentId}
                    onClick={handleRemoveAttachment}
                    aria-label={`remove attachment ${attachmentId} of ${prescriptionIndex}`}
                  >
                    <CloseIcon />
                  </IconButton>
                )
              }
            >
              <Link
                href={URL.createObjectURL(attachment.file)}
                download={attachment.fileName}
                target='_blank'
              >
                {attachment.filename}
              </Link>
            </ListItem>
          )
        )}
      </List>
    </Box>
  );
};

PrescriptionsFileList.propTypes = {
  prescriptionIndex: PropTypes.number.isRequired,
};

const ProviderSearch = ({ prescriptionIndex }) => {
  const form = useForm();
  const formState = useFormState();
  const selectedProvider = formState.values.prescriptionInput[prescriptionIndex].providerData;
  const providers = formState.values.practiceProviders;

  const handleProviderSelect = (_, provider) => {
    form.change(`prescriptionInput[${prescriptionIndex}].providerInput.npi`, provider?.npi);
    form.change(`prescriptionInput[${prescriptionIndex}].providerInput.dea`, provider?.dea);
    form.change(`prescriptionInput[${prescriptionIndex}].providerInput.title`, provider?.title);
    form.change(
      `prescriptionInput[${prescriptionIndex}].providerInput.firstName`,
      provider?.firstName
    );
    form.change(
      `prescriptionInput[${prescriptionIndex}].providerInput.middleName`,
      provider?.middleName
    );
    form.change(
      `prescriptionInput[${prescriptionIndex}].providerInput.lastName`,
      provider?.lastName
    );
    form.change(
      `prescriptionInput[${prescriptionIndex}].providerInput.phoneNumber`,
      provider?.phoneNumber
    );

    form.change(`prescriptionInput[${prescriptionIndex}].providerData`, provider);

    if (provider === null) {
      form.change(`prescriptionInput[${prescriptionIndex}].providerData`, undefined);
    }
  };

  return (
    <Autocomplete
      sx={{ mb: 3 }}
      fullWidth
      options={isNil(providers) ? [] : providers}
      clearOnBlur
      value={selectedProvider || null}
      onChange={handleProviderSelect}
      getOptionLabel={(option) => `${option.firstName} ${option.lastName} - ${option.npi}`}
      isOptionEqualToValue={(option, value) => option === value}
      noOptionsText={<Typography sx={{ fontSize: '1em' }}>No providers found.</Typography>}
      renderOption={(props, provider) => {
        return (
          // eslint-disable-next-line react/jsx-props-no-spreading, react/prop-types
          <ListItem {...props} key={props.id}>
            <ListItemText
              primary={`${provider.firstName} ${provider.lastName}`}
              secondary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant='body2' component='span' sx={{ fontSize: '0.875rem' }}>
                    NPI: {provider.npi}
                  </Typography>
                  {provider.npi && (
                    <Box
                      sx={{
                        display: 'inline-flex',
                        '& svg': { fontSize: '1.2rem' },
                      }}
                    >
                      <VerifiedTag
                        status={
                          ProviderVerificationStatus[provider.verificationStatus] ||
                          VerificationStatus.UNVERIFIED
                        }
                      />
                    </Box>
                  )}
                </Box>
              }
            />
          </ListItem>
        );
      }}
      renderInput={(params) => {
        return (
          <MuiTextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            label='Search Provider by Name or NPI'
            InputProps={{
              ...params.InputProps,
              endAdornment: params.InputProps.endAdornment,
            }}
          />
        );
      }}
    />
  );
};

ProviderSearch.propTypes = {
  prescriptionIndex: PropTypes.number.isRequired,
};

const ProviderForm = ({ prescriptionIndex }) => {
  return (
    <Grid container columnSpacing={1}>
      <Grid xs={12}>
        <Typography component='h3' variant='h6' sx={{ mb: 3 }}>
          Provider Information
        </Typography>
      </Grid>

      <ProviderSearch prescriptionIndex={prescriptionIndex} />

      <Grid xs={4}>
        <TextField
          id='Prescriptions-ProviderForm-npi-input'
          name={`prescriptionInput[${prescriptionIndex}].providerInput.npi`}
          label='NPI'
          required
          disabled
          validations={[validateNPI()]}
          readOnly
        />
      </Grid>

      <Grid xs={4}>
        <TextField
          id='Prescriptions-ProviderForm-dea-input'
          name={`prescriptionInput[${prescriptionIndex}].providerInput.dea`}
          label='DEA'
          disabled
          readOnly
        />
      </Grid>

      <Grid xs={4}>
        <TextField
          id='Prescriptions-ProviderForm-title-input'
          name={`prescriptionInput[${prescriptionIndex}].providerInput.title`}
          label='Title'
          disabled
          readOnly
        />
      </Grid>

      <Grid xs={4}>
        <TextField
          id='Prescriptions-ProviderForm-firstName-input'
          name={`prescriptionInput[${prescriptionIndex}].providerInput.firstName`}
          label='First Name'
          required
          disabled
          readOnly
        />
      </Grid>

      <Grid xs={4}>
        <TextField
          id='Prescriptions-ProviderForm-middleName-input'
          name={`prescriptionInput[${prescriptionIndex}].providerInput.middleName`}
          label='Middle Name'
          disabled
          readOnly
        />
      </Grid>

      <Grid xs={4}>
        <TextField
          id='Prescriptions-ProviderForm-lastName-input'
          name={`prescriptionInput[${prescriptionIndex}].providerInput.lastName`}
          label='Last Name'
          required
          disabled
          readOnly
        />
      </Grid>

      <TextField
        id='Prescriptions-ProviderForm-phoneNumber-input'
        name={`prescriptionInput[${prescriptionIndex}].providerInput.phoneNumber`}
        label='Phone Number'
        required
        disabled
        validations={[validatePhoneNumber()]}
        readOnly
      />
    </Grid>
  );
};

ProviderForm.propTypes = {
  prescriptionIndex: PropTypes.number.isRequired,
};

const SigAutoComplete = ({ prescriptionIndex }) => {
  const form = useForm();
  const formState = useFormState();
  const [loading, setLoading] = useState(false);
  const [sigs, setSigs] = useState(null);

  const handleSigSelect = (_, value) =>
    form.change(`prescriptionInput[${prescriptionIndex}].directions`, value);

  const handleInputChange = (_, value) =>
    form.change(`prescriptionInput[${prescriptionIndex}].directions`, value);

  const handleSigSearch = useMemo(
    () =>
      debounce(({ searchText }) => {
        listSigsApi({ searchText })
          .then((sigResults) => setSigs(uniq(map(sigResults, 'sig'))))
          .finally(() => setLoading(false));
      }, 500),
    []
  );

  useEffect(() => {
    setSigs(null);
    if (formState.values.prescriptionInput[prescriptionIndex]?.directions?.length > 2) {
      setLoading(true);
      handleSigSearch({
        searchText: formState.values.prescriptionInput[prescriptionIndex]?.directions,
      });
    }
  }, [
    formState.values.prescriptionInput,
    formState.values.prescriptionInput.directions,
    handleSigSearch,
    prescriptionIndex,
  ]);

  return (
    <Autocomplete
      freeSolo
      fullWidth
      options={isNil(sigs) ? [] : sigs}
      loading={loading}
      value={formState.values.prescriptionInput[prescriptionIndex]?.directions || null}
      onChange={handleSigSelect}
      onInputChange={handleInputChange}
      renderInput={(params) => {
        return (
          <TextField
            params={params}
            id='Prescriptions-SigAutoComplete-directions-input'
            name={`prescriptionInput[${prescriptionIndex}].directions`}
            label='Directions'
            required
            maxLength={180}
          />
        );
      }}
    />
  );
};

SigAutoComplete.propTypes = {
  prescriptionIndex: PropTypes.number.isRequired,
};

// POM-5983: secondary line under a strength option — "<pack size value><unit> <form alias>", e.g.
// "0.6mL VL" (no space between value and unit). Skips any missing part; returns '' when there is
// nothing to show. Pack/form come from the catalog variant surfaced by getPartnerCatalog.
const formatVariantPackForm = (variant) => {
  if (!variant) return '';
  const packSize =
    variant.packSizeValue && variant.packSizeUnit
      ? `${variant.packSizeValue}${variant.packSizeUnit}`
      : variant.packSizeValue || variant.packSizeUnit || '';
  return compact([packSize, variant.formAlias]).join(' ');
};

const PrescriptionForm = ({ prescriptionIndex, handleChange, expanded }) => {
  const form = useForm();
  const formState = useFormState();
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const selectedDrug = formState.values.prescriptionInput[prescriptionIndex];
  const { partnerFormulary, compoundingReasons = [] } = formState.values;
  const formularyVariants = find(partnerFormulary, { drugName: selectedDrug.drugName })?.variants;
  const strengthsAndAvailability = reduce(
    formularyVariants,
    (result, v) => ({ ...result, [v.strength]: v.isAvailable }),
    {}
  );

  const currentStrength = formState.values.prescriptionInput[prescriptionIndex].strength;
  const catalogId = find(formularyVariants, { strength: currentStrength })?.catalogId;
  form.change(`prescriptionInput[${prescriptionIndex}].catalogId`, catalogId);

  const selectedVariant = find(formularyVariants, { strength: currentStrength });
  const isCentralFillDrug = !!selectedVariant?.isCompoundingEligible;

  const handleRemovePrescription = () => {
    showInfoDialog({
      title: 'Remove prescription',
      message: 'Are you sure you want to remove this prescription from this order?',
      confirmButtonText: 'Confirm',
      isWarning: true,
      cancelable: true,
      callback: () => {
        form.change('prescriptionInput', [
          ...slice(formState.values.prescriptionInput, 0, prescriptionIndex),
          ...slice(formState.values.prescriptionInput, prescriptionIndex + 1),
        ]);
        handleChange(prescriptionIndex)(undefined, false);
      },
    });
  };

  useLayoutEffect(() => {
    if (
      !includes(
        keys(strengthsAndAvailability),
        form.getState().values.prescriptionInput[prescriptionIndex]?.strength
      )
    ) {
      form.change(`prescriptionInput[${prescriptionIndex}].strength`, undefined);
    }
    if (keys(strengthsAndAvailability)?.length === 1 && first(values(strengthsAndAvailability))) {
      form.change(
        `prescriptionInput[${prescriptionIndex}].strength`,
        first(keys(strengthsAndAvailability))
      );
    }
  }, [strengthsAndAvailability, form, prescriptionIndex]);

  useLayoutEffect(() => {
    // POM-5747: carry the catalog Product ID for every drug (not only compounding) so the order
    // flow persists it end-to-end; reasonForCompounding stays compounding-only.
    form.change(`prescriptionInput[${prescriptionIndex}].productId`, selectedVariant?.productId);
    if (!isCentralFillDrug) {
      form.change(`prescriptionInput[${prescriptionIndex}].reasonForCompounding`, undefined);
    }
    // Carried through the same way as productId, so the Review step can show the catalog's
    // Package Size and Form Alias next to the Drug Strength without a second lookup.
    form.change(
      `prescriptionInput[${prescriptionIndex}].packSizeValue`,
      selectedVariant?.packSizeValue
    );
    form.change(
      `prescriptionInput[${prescriptionIndex}].packSizeUnit`,
      selectedVariant?.packSizeUnit
    );
    form.change(`prescriptionInput[${prescriptionIndex}].formAlias`, selectedVariant?.formAlias);
  }, [
    isCentralFillDrug,
    selectedVariant?.productId,
    selectedVariant?.packSizeValue,
    selectedVariant?.packSizeUnit,
    selectedVariant?.formAlias,
    form,
    prescriptionIndex,
  ]);

  return (
    <>
      <InfoDialog />
      <Accordion
        expanded={expanded}
        onChange={handleChange(prescriptionIndex)}
        id={`Prescription-${prescriptionIndex}-header`}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography
            component='span'
            sx={{ width: '20%', flexShrink: 0, display: 'flex', alignItems: 'center' }}
            color='primary'
          >
            Prescription #{prescriptionIndex + 1}
            {!isEmpty(formState.errors?.prescriptionInput?.[prescriptionIndex]) && (
              <Tooltip title='Information Needed'>
                <ErrorOutlineIcon color='error' sx={{ ml: 2 }} />
              </Tooltip>
            )}
          </Typography>

          <Typography component='span' sx={{ color: 'text.secondary' }}>
            {selectedDrug.drugName}
            {formState.values.prescriptionInput[prescriptionIndex].strength
              ? ` - ${formState.values.prescriptionInput[prescriptionIndex].strength}`
              : ''}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ position: 'relative' }}>
          <Button
            variant='outlined'
            color='error'
            sx={{ position: 'absolute', top: 8, right: 16 }}
            onClick={handleRemovePrescription}
          >
            Remove
          </Button>
          <ProviderForm prescriptionIndex={prescriptionIndex} />
          <Grid container columnSpacing={1}>
            <Grid xs={12}>
              <Typography component='h3' variant='h6' sx={{ mb: 3 }}>
                Prescription Information
              </Typography>
            </Grid>
            <Grid xs={7}>
              <SelectField
                id='Prescriptions-MedicationForm-drugName-input'
                name={`prescriptionInput[${prescriptionIndex}].drugName`}
                label='Drug Name'
                required
              >
                {hasNoOrderableCatalog(partnerFormulary) && (
                  <MenuItem value='' disabled>
                    {NO_PRODUCTS_AVAILABLE_MESSAGE}
                  </MenuItem>
                )}
                {map(sortBy(partnerFormulary, 'drugName'), ({ drugName, variants }) => {
                  const hasCentralFillVariant = some(variants, { isCompoundingEligible: true });
                  return (
                    <MenuItem value={drugName} key={drugName}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          gap: 1,
                        }}
                      >
                        <span>{drugName}</span>
                        {hasCentralFillVariant && (
                          <Chip
                            label='Comp'
                            size='small'
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: 'primary.main',
                              color: 'common.white',
                            }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </SelectField>
            </Grid>

            <Grid xs={5}>
              <SelectField
                id='Prescriptions-MedicationForm-strength-input'
                name={`prescriptionInput[${prescriptionIndex}].strength`}
                label='Strength'
                required
                disabled={!selectedDrug.drugName}
                renderValue={(value) => value}
              >
                {map(strengthsAndAvailability, (isAvailable, drugStrength) => {
                  const packForm = formatVariantPackForm(
                    find(formularyVariants, { strength: drugStrength })
                  );
                  return (
                    <MenuItem value={drugStrength} key={drugStrength} disabled={!isAvailable}>
                      <Box>
                        <Typography variant='body2'>
                          {drugStrength} {!isAvailable ? '- Out of Stock' : ''}
                        </Typography>
                        {packForm && (
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ display: 'block', lineHeight: 1.2 }}
                          >
                            {packForm}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </SelectField>
            </Grid>

            {isCentralFillDrug && (
              <Grid xs={12}>
                <SelectField
                  id='Prescriptions-MedicationForm-reasonForCompounding-input'
                  name={`prescriptionInput[${prescriptionIndex}].reasonForCompounding`}
                  label='Reason for Compounding'
                >
                  {map(compoundingReasons, ({ code, description }) => (
                    <MenuItem value={code} key={code}>
                      {description}
                    </MenuItem>
                  ))}
                </SelectField>
              </Grid>
            )}

            <Grid xs={6}>
              <TextField
                id='Prescriptions-MedicationForm-quantity-input'
                name={`prescriptionInput[${prescriptionIndex}].quantity`}
                label='Quantity'
                required
                validations={[validateGreaterThan('Must be greater than 0', 0)]}
              />
            </Grid>

            <Grid xs={6}>
              <SelectField
                id='Prescriptions-MedicationForm-refills-input'
                name={`prescriptionInput[${prescriptionIndex}].refills`}
                label='Refills'
                required
                lexicographicSort={false}
              >
                {map(new Array(12), (_, index) => (
                  <MenuItem value={index} key={index}>
                    {index}
                  </MenuItem>
                ))}
              </SelectField>
            </Grid>

            <SigAutoComplete prescriptionIndex={prescriptionIndex} />

            <TextField
              id='Prescriptions-MedicationForm-notes-input'
              name={`prescriptionInput[${prescriptionIndex}].notes`}
              label='Notes'
              rows={3}
              multiline
              maxLength={1000}
            />

            <Grid xs={6}>
              <DateField
                id='Prescriptions-MedicationForm-expirationDate-input'
                name={`prescriptionInput[${prescriptionIndex}].expirationDate`}
                label='Expiration Date'
                minDate={new Date(new Date().setDate(new Date().getDate() + 3))}
              />
            </Grid>

            <Grid xs={6}>
              <DateField
                id='Prescriptions-MedicationForm-dateWritten-input'
                name={`prescriptionInput[${prescriptionIndex}].dateWritten`}
                label='Date Written'
                maxDate={new Date()}
                required
              />
            </Grid>

            <Grid xs={6} sx={{ alignSelf: 'center', pt: 2, pl: 1 }}>
              <CheckboxField
                id='Prescriptions-MedicationForm-dispenseAsWritten-checkbox'
                name={`prescriptionInput[${prescriptionIndex}].dispenseAsWritten`}
                label='Dispense As Written'
              />
            </Grid>

            <Grid xs={12}>
              <Box>
                <Typography component='h3' variant='h6' sx={{ mb: 3 }}>
                  Prescription Records (Required)
                </Typography>

                <Grid container columnSpacing={1}>
                  <Grid xs={6}>
                    <PrescriptionRecordFileUploader prescriptionIndex={prescriptionIndex} />
                  </Grid>

                  <Grid xs={6}>
                    <PrescriptionsFileList prescriptionIndex={prescriptionIndex} />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const OrderFormEnabler = () => {
  const form = useForm();
  const formState = useFormState();

  useEffect(() => {
    form.change(
      'numberOfItems',
      formState.values.prescriptionInput.length + formState.values.otcInput.length
    );
  }, [form, formState.values]);

  return (
    <Box sx={visuallyHidden}>
      <TextField
        id='numberOfItems'
        name='numberOfItems'
        label='number of items'
        required
        validations={[validateGreaterThan('Must be more than 1 item', 0)]}
      />
    </Box>
  );
};

const OtcForm = ({ otcIndex }) => {
  const form = useForm();
  const formState = useFormState();
  const { otcItems } = useOtcItemsList();
  const { InfoDialog, showInfoDialog } = useInfoDialog();

  const handleRemoveOtc = () => {
    showInfoDialog({
      title: 'Remove OTC',
      message: 'Are you sure you want to remove this item from this order?',
      confirmButtonText: 'Confirm',
      isWarning: true,
      cancelable: true,
      callback: () => {
        form.change('otcInput', [
          ...slice(formState.values.otcInput, 0, otcIndex),
          ...slice(formState.values.otcInput, otcIndex + 1),
        ]);
      },
    });
  };

  return (
    <>
      <InfoDialog />
      <TableRow>
        <TableCell sx={{ width: 16 }} id={`OtcForm-${otcIndex}-header`}>
          {otcIndex + 1}
        </TableCell>
        <TableCell sx={{ width: 150, p: 0.5 }}>
          <Field
            name={`otcInput.[${otcIndex}].otcCatalogId`}
            id={`otcList-${otcIndex}-input`}
            label={`OTC ${otcIndex + 1}`}
            validate={composeValidations([validateRequired()])}
            render={({ input, meta }) => {
              const showError = !!meta.error && !!meta.touched;
              const displayedError = meta.error;

              return (
                <Tooltip placement='top' title={displayedError}>
                  <MuiTextField
                    variant='outlined'
                    id={`otcList-${otcIndex}-input`}
                    hiddenLabel
                    fullWidth
                    required
                    error={showError}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...input}
                    // https://github.com/final-form/react-final-form/issues/529
                    value={input.value || ''}
                    select
                    size='small'
                  >
                    {map(otcItems, (otc) => (
                      <MenuItem value={otc.catalogId} key={otc.catalogId}>
                        {otc.name}
                        {otc.strength && ` - ${otc.strength}`}
                      </MenuItem>
                    ))}
                  </MuiTextField>
                </Tooltip>
              );
            }}
          />
        </TableCell>
        <TableCell sx={{ width: 200 }}>
          {find(otcItems, {
            catalogId: formState.values.otcInput[otcIndex].otcCatalogId,
          })?.description || 'N/A'}
        </TableCell>
        <TableCell sx={{ width: 200 }}>
          <Field
            name={`otcInput.[${otcIndex}].instructions`}
            id={`otcList-${otcIndex}-instructions-input`}
            label={`OTC ${otcIndex + 1} instructions`}
            validate={composeValidations([validateASCIIChars()])}
            render={({ input, meta }) => {
              const showError = !!meta.error && !!meta.touched;
              const displayedError = meta.error;

              return (
                <Tooltip placement='top' title={displayedError}>
                  <MuiTextField
                    variant='outlined'
                    id={`otcList-${otcIndex}-instructions-input`}
                    hiddenLabel
                    fullWidth
                    error={showError}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...input}
                    // https://github.com/final-form/react-final-form/issues/529
                    value={input.value || ''}
                    size='small'
                  />
                </Tooltip>
              );
            }}
          />
        </TableCell>
        <TableCell sx={{ width: 75, p: 0.5 }}>
          <Field
            name={`otcInput.[${otcIndex}].quantity`}
            id={`otcList-${otcIndex}-quantity-input`}
            label={`OTC ${otcIndex + 1} quantity`}
            validate={composeValidations([
              validateRequired(),
              validateGreaterThan('Must be greater than 0', 0),
            ])}
            render={({ input, meta }) => {
              const showError = !!meta.error && !!meta.touched;
              const displayedError = meta.error;

              return (
                <Tooltip placement='top' title={displayedError}>
                  <MuiTextField
                    variant='outlined'
                    id={`otcList-${otcIndex}-quantity-input`}
                    hiddenLabel
                    fullWidth
                    required
                    error={showError}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...input}
                    // https://github.com/final-form/react-final-form/issues/529
                    value={input.value || ''}
                    size='small'
                    inputProps={{
                      textAlign: 'right',
                    }}
                  />
                </Tooltip>
              );
            }}
          />
        </TableCell>
        <TableCell>
          <IconButton size='small' onClick={handleRemoveOtc}>
            <DeleteOutlineIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    </>
  );
};

OtcForm.propTypes = {
  otcIndex: PropTypes.number.isRequired,
};

const OtcList = () => {
  const formState = useFormState();

  return (
    <Box sx={{ p: 0.5, minHeight: '60vh' }}>
      <TableContainer sx={{ mt: -2 }}>
        <Table size='small' padding='none'>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 16, color: 'primary.main' }}>#</TableCell>
              <TableCell sx={{ width: 150, color: 'primary.main' }}>Name</TableCell>
              <TableCell sx={{ width: 200, color: 'primary.main' }}>Description</TableCell>
              <TableCell sx={{ width: 200, color: 'primary.main' }} align='left'>
                Instructions
              </TableCell>
              <TableCell sx={{ width: 75, color: 'primary.main' }} align='right'>
                Quantity
              </TableCell>
              <TableCell sx={{ width: 16 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {map(formState.values.otcInput, (_, index) => (
              <OtcForm key={index} otcIndex={index} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

PrescriptionForm.propTypes = {
  prescriptionIndex: PropTypes.number.isRequired,
  handleChange: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
};

const MedicationForm = () => {
  const form = useForm();
  const formState = useFormState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState(0);
  const { patientData } = formState.values;
  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);
  const { otcItems } = useOtcItemsList();

  const handleChange = (panel) => (_, isExpanded) => setExpanded(isExpanded ? panel : false);

  const handleAddPrescription = () => {
    handleCloseMenu();
    const newState = [...formState.values.prescriptionInput, { dateWritten: new Date() }];
    form.change('prescriptionInput', newState);
    setExpanded(newState.length - 1);
    setTimeout(() => {
      document.getElementById(`Prescription-${newState.length - 1}-header`)?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 150);
  };

  const addOtc = () => {
    handleCloseMenu();
    const newState = [...formState.values.otcInput, {}];
    form.change('otcInput', newState);
    setTimeout(() => {
      document.getElementById(`OtcForm-${newState.length - 1}-header`)?.scrollIntoView({
        behavior: 'smooth',
      });
    }, 150);
  };

  return (
    <Box>
      <OrderFormEnabler />
      <Typography component='h3' variant='h5' sx={{ mb: 3, width: '100%' }} color='primary'>
        Order Items For {patientData?.firstName} {patientData?.lastName}
      </Typography>

      {!isEmpty(formState.values.prescriptionInput) && (
        <>
          <Typography component='h4' variant='h6' sx={{ mb: 3, width: '100%' }} color='primary'>
            Medication(s)
          </Typography>
          {map(formState.values.prescriptionInput, (_, index) => (
            <PrescriptionForm
              key={index}
              prescriptionIndex={index}
              handleChange={handleChange}
              expanded={expanded === index}
            />
          ))}
        </>
      )}

      {!isEmpty(formState.values.otcInput) && (
        <>
          <Typography component='h4' variant='h6' sx={{ my: 3, width: '100%' }} color='primary'>
            OTC(s)
          </Typography>
          <OtcList />
        </>
      )}

      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'row-reverse' }}>
        <Button
          variant='contained'
          sx={{ width: 100 }}
          size='large'
          id='OrderForm-add-button'
          onClick={isEmpty(otcItems) ? handleAddPrescription : handleOpenMenu}
        >
          Add
        </Button>
        <Menu id='order-menu' anchorEl={anchorEl} open={!!anchorEl} onClose={handleCloseMenu}>
          <MenuItem onClick={handleAddPrescription}>Prescription</MenuItem>
          <MenuItem onClick={addOtc} sx={isEmpty(otcItems) ? visuallyHidden : undefined}>
            OTC
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

const ShippingForm = () => {
  const formState = useFormState();
  const { shippingMethod } = useFormState().values;

  return (
    <Box>
      <Typography component='h3' variant='h5' sx={{ mb: 3, width: '100%' }} color='primary'>
        Shipping
      </Typography>

      <Grid container>
        <Grid xs={6}>
          <SelectField
            name='shippingOption'
            label='Shipping Method'
            required
            id='CreateOrderForm-ShippingForm-shippingOption-dropdown'
          >
            {map(formState.values.shippingOptions, (shippingOption) => (
              <MenuItem value={shippingOption.optionCode} key={shippingOption.optionCode}>
                {shippingOption.shippingOption}
              </MenuItem>
            ))}
          </SelectField>
        </Grid>

        <Grid xs={12}>
          <RadioGroupField
            name='shippingMethod'
            required
            label='Ship to'
            id='CreateOrderForm-ShippingForm-shippingMethod-radio'
            row
          >
            <FormControlLabel value='patient' control={<Radio />} label='Patient' />
            <FormControlLabel value='practice' control={<Radio />} label='Practice' />
            <FormControlLabel value='new' control={<Radio />} label='New Address' />
          </RadioGroupField>
        </Grid>

        {shippingMethod === 'patient' && (
          <Grid sx={{ mt: 2 }} xs={6}>
            <div>
              <Grid container columnSpacing={1}>
                <TextField
                  id='Prescriptions-ShippingAddress-line1-input'
                  name='patientAddress.line1'
                  label='Address Line 1'
                  maxLength={255}
                  required
                  disabled
                />

                <TextField
                  id='Prescriptions-ShippingAddress-line2-input'
                  name='patientAddress.line2'
                  label='Address Line 2'
                  maxLength={255}
                  disabled
                />

                <Grid xs={4}>
                  <TextField
                    id='Prescriptions-ShippingAddress-city-input'
                    name='patientAddress.city'
                    label='City'
                    required
                    disabled
                  />
                </Grid>

                <Grid xs={4}>
                  <SelectField
                    id='Prescriptions-ShippingAddress-state-input'
                    name='patientAddress.state'
                    label='State'
                    required
                    disabled
                  >
                    {map(usStateAbbreviation, ({ name, abbreviation }) => (
                      <MenuItem value={abbreviation} key={abbreviation}>
                        {name}
                      </MenuItem>
                    ))}
                  </SelectField>
                </Grid>

                <Grid xs={4}>
                  <TextField
                    id='Prescriptions-ShippingAddress-zip-input'
                    name='patientAddress.zip'
                    label='Zip'
                    required
                    validations={[validateZipCode('Must be valid zip')]}
                    disabled
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        )}

        {shippingMethod === 'practice' && (
          <Grid sx={{ mt: 2 }} xs={6}>
            <div>
              <Grid container columnSpacing={1}>
                <TextField
                  id='Prescriptions-ShippingAddress-line1-input'
                  name='practiceDetails.line1'
                  label='Address Line 1'
                  maxLength={255}
                  required
                  disabled
                />

                <TextField
                  id='Prescriptions-ShippingAddress-line2-input'
                  name='practiceDetails.line2'
                  label='Address Line 2'
                  maxLength={255}
                  disabled
                />

                <Grid xs={4}>
                  <TextField
                    id='Prescriptions-ShippingAddress-city-input'
                    name='practiceDetails.city'
                    label='City'
                    required
                    disabled
                  />
                </Grid>

                <Grid xs={4}>
                  <SelectField
                    id='Prescriptions-ShippingAddress-state-input'
                    name='practiceDetails.state'
                    label='State'
                    required
                    disabled
                  >
                    {map(usStateAbbreviation, ({ name, abbreviation }) => (
                      <MenuItem value={abbreviation} key={abbreviation}>
                        {name}
                      </MenuItem>
                    ))}
                  </SelectField>
                </Grid>

                <Grid xs={4}>
                  <TextField
                    id='Prescriptions-ShippingAddress-zip-input'
                    name='practiceDetails.zip'
                    label='Zip'
                    required
                    validations={[validateZipCode('Must be valid zip')]}
                    disabled
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        )}

        {shippingMethod === 'new' && (
          <Grid sx={{ mt: 2 }} xs={6}>
            <div>
              <Grid container columnSpacing={1}>
                <TextField
                  id='Prescriptions-ShippingAddress-line1-input'
                  name='shippingInput.addressLine1'
                  label='Address Line 1'
                  maxLength={255}
                  required
                  validations={[
                    validateCombinedAddressLength(
                      'shippingInput.addressLine1',
                      'shippingInput.addressLine2'
                    ),
                  ]}
                />

                <TextField
                  id='Prescriptions-ShippingAddress-line2-input'
                  name='shippingInput.addressLine2'
                  label='Address Line 2'
                  maxLength={255}
                  validations={[
                    validateCombinedAddressLength(
                      'shippingInput.addressLine1',
                      'shippingInput.addressLine2'
                    ),
                  ]}
                />

                <Grid xs={4}>
                  <TextField
                    id='Prescriptions-ShippingAddress-city-input'
                    name='shippingInput.addressCity'
                    label='City'
                    required
                  />
                </Grid>

                <Grid xs={4}>
                  <SelectField
                    id='Prescriptions-ShippingAddress-state-input'
                    name='shippingInput.addressState'
                    label='State'
                    required
                  >
                    {map(usStateAbbreviation, ({ name, abbreviation }) => (
                      <MenuItem value={abbreviation} key={abbreviation}>
                        {name}
                      </MenuItem>
                    ))}
                  </SelectField>
                </Grid>

                <Grid xs={4}>
                  <TextField
                    id='Prescriptions-ShippingAddress-zip-input'
                    name='shippingInput.addressZip'
                    label='Zip'
                    required
                    validations={[validateZipCode('Must be valid zip')]}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const LabelValuePair = ({ label, value }) => {
  return (
    <Grid xs={12}>
      <div>
        <Grid container sx={{ minHeight: 36 }}>
          <Grid xs={3.5}>
            <Typography sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}>
              {label}
            </Typography>
          </Grid>
          <Grid xs={7}>
            <Typography component='div'>{value}</Typography>
          </Grid>
        </Grid>
      </div>
    </Grid>
  );
};

LabelValuePair.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

LabelValuePair.defaultProps = {
  value: null,
};

const ReviewForm = () => {
  const formState = useFormState();
  const partnerContact = usePartnerContactForPatient({
    mpi: formState.values.patientData?.mpi,
    partnerUuid: formState.values.partnerId,
  });
  const { firstName, lastName } = useSelector(({ auth }) => auth);
  const { otcItems } = useOtcItemsList();

  useEffect(() => {
    document.getElementById(`ReviewForm-header`)?.scrollIntoView();
  }, []);

  return (
    <Box sx={{ maxWidth: 1000 }}>
      <Typography
        component='h3'
        variant='h5'
        sx={{ mb: 3, width: '100%' }}
        color='primary'
        id='ReviewForm-header'
      >
        Review
      </Typography>

      <Box sx={{ width: '100%' }}>
        <Grid container>
          <Typography component='h4' variant='h6' sx={{ mb: 1 }} color='primary'>
            Patient
          </Typography>

          <LabelValuePair label='First Name' value={formState.values.patientData.firstName} />
          <LabelValuePair label='Last Name' value={formState.values.patientData.lastName} />
          <LabelValuePair
            label='Date of Birth'
            value={format(
              parse(formState.values.patientData.dob, 'yyyy-MM-dd', new Date()),
              'yyyy-MM-dd'
            )}
          />
          <LabelValuePair label='Gender' value={gender[formState.values.patientData.gender]} />
          <LabelValuePair
            label='Email'
            value={<PartnerContactValue partnerContact={partnerContact} contactKind='email' />}
          />
          <LabelValuePair
            label='Phone Number'
            value={<PartnerContactValue partnerContact={partnerContact} contactKind='phone' />}
          />
        </Grid>
        <Divider sx={{ my: 1 }} />
      </Box>

      <Box sx={{ width: '100%' }}>
        <Grid container>
          <Grid>
            <div>
              <Grid container direction='column'>
                <Typography component='h4' variant='h6' sx={{ mb: 1 }} color='primary'>
                  Patient Address
                </Typography>
              </Grid>

              <Box sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}>
                <div>{formState.values.patientAddress.line1}</div>
                <div>{formState.values.patientAddress.line2}</div>
                <div>
                  {formState.values.patientAddress.city},&nbsp;
                  {formState.values.patientAddress.state} {formState.values.patientAddress.zip}
                </div>
              </Box>
            </div>
          </Grid>
        </Grid>
        <Divider sx={{ mt: 3, mb: 1 }} />
      </Box>

      <Box sx={{ width: '100%' }}>
        <Typography component='h4' variant='h6' sx={{ mb: 1 }} color='primary'>
          Shipping
        </Typography>

        <Grid container>
          <div>
            <Grid container direction='column'>
              <Typography component='h4' variant='body1' sx={{ mb: 1 }} color='primary'>
                Shipping Address
              </Typography>
            </Grid>

            {formState.values.shippingMethod === 'new' && (
              <Box sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}>
                <div>{formState.values.shippingInput.addressLine1}</div>
                <div>{formState.values.shippingInput.addressLine2}</div>
                <div>
                  {formState.values.shippingInput.addressCity},&nbsp;
                  {formState.values.shippingInput.addressState}{' '}
                  {formState.values.shippingInput.addressZip}
                </div>
              </Box>
            )}

            {formState.values.shippingMethod === 'patient' && (
              <Box sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}>
                <div>{formState.values.patientAddress.line1}</div>
                <div>{formState.values.patientAddress.line2}</div>
                <div>
                  {formState.values.patientAddress.city},&nbsp;
                  {formState.values.patientAddress.state} {formState.values.patientAddress.zip}
                </div>
              </Box>
            )}

            {formState.values.shippingMethod === 'practice' && (
              <Box sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}>
                <div>{formState.values.practiceDetails.line1}</div>
                <div>{formState.values.practiceDetails.line2}</div>
                <div>
                  {formState.values.practiceDetails.city},&nbsp;
                  {formState.values.practiceDetails.state}&nbsp;
                  {formState.values.practiceDetails.zip}
                </div>
              </Box>
            )}
          </div>
        </Grid>

        <Grid container sx={{ mt: 2 }}>
          <div>
            <Grid container direction='column'>
              <Typography component='h4' variant='body1' sx={{ mb: 1 }} color='primary'>
                Shipping Option
              </Typography>
            </Grid>

            <Typography sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}>
              {formState.values.shippingOption
                ? find(formState.values.shippingOptions, {
                    optionCode: formState.values.shippingOption,
                  })?.shippingOption
                : 'N/A'}
            </Typography>
          </div>
        </Grid>

        <Divider sx={{ mt: 3, mb: 1 }} />
      </Box>

      <Box sx={{ width: '100%' }}>
        <Typography component='h4' variant='h5' sx={{ mb: 1 }} color='primary'>
          Order Items
        </Typography>
        {!isEmpty(formState.values.prescriptionInput) && (
          <Typography component='h5' variant='h6' sx={{ mb: 1 }} color='primary'>
            Medication(s)
          </Typography>
        )}
        {map(formState.values.prescriptionInput, (prescription, index) => (
          <Grid container key={index}>
            <Grid xs={12}>
              <Typography component='h4' variant='h6' sx={{ mb: 1 }} color='primary'>
                Prescription #{index + 1}
              </Typography>
            </Grid>
            <Grid xs={6}>
              <div>
                <Grid container key={index}>
                  <LabelValuePair label='Drug Name' value={prescription.drugName} />
                  <LabelValuePair label='Strength' value={prescription.strength} />
                  <LabelValuePair
                    label='Package Size'
                    value={
                      prescription.packSizeValue && prescription.packSizeUnit
                        ? `${prescription.packSizeValue}${prescription.packSizeUnit}`
                        : prescription.packSizeValue || prescription.packSizeUnit
                    }
                  />
                  <LabelValuePair label='Form' value={prescription.formAlias} />
                  <LabelValuePair label='Quantity' value={prescription.quantity} />
                  <LabelValuePair label='Refills' value={prescription.refills} />
                  <LabelValuePair label='Directions' value={prescription.directions} />
                  <LabelValuePair label='Notes' value={prescription.notes} />
                  <LabelValuePair
                    label='Expiration Date'
                    value={format(
                      prescription.expirationDate || new Date().setDate(new Date().getDate() + 365),
                      'MM/dd/yyyy'
                    )}
                  />
                  <LabelValuePair
                    label='Date Written'
                    value={format(prescription.dateWritten, 'MM/dd/yyyy')}
                  />
                  <Grid
                    xs={3.5}
                    sx={{ color: (theme) => lighten(theme.palette.text.primary, 0.3) }}
                  >
                    Prescription Record(s)
                  </Grid>
                  <Box>
                    {map(prescription.attachments, (attachment) => (
                      <div key={attachment.filename}>{attachment.filename}</div>
                    ))}
                  </Box>
                </Grid>
              </div>
            </Grid>

            <Grid xs={6}>
              <div>
                <Grid container key={index}>
                  <LabelValuePair
                    label='Practice Name'
                    value={formState.values.practiceDetails.practiceName}
                  />
                  <LabelValuePair label='NPI' value={prescription.providerInput.npi} />
                  <LabelValuePair label='DEA' value={prescription.providerInput.dea} />
                  <LabelValuePair label='Title' value={prescription.providerInput.title} />
                  <LabelValuePair label='First Name' value={prescription.providerInput.firstName} />
                  <LabelValuePair
                    label='Middle Name'
                    value={prescription.providerInput.middleName}
                  />
                  <LabelValuePair label='Last Name' value={prescription.providerInput.lastName} />
                  <LabelValuePair
                    label='Phone Number'
                    value={prescription.providerInput.phoneNumber}
                  />
                </Grid>
              </div>
            </Grid>
          </Grid>
        ))}

        {!isEmpty(formState.values.otcInput) && (
          <Typography component='h5' variant='h6' sx={{ mb: 1 }} color='primary'>
            Otc(s)
          </Typography>
        )}
        {map(formState.values.otcInput, (otc, index) => (
          <Grid container key={index} component={Paper} sx={{ mb: 0.5, p: 1 }}>
            <Grid xs={12}>
              <Typography component='h4' variant='body' sx={{ mb: 1 }} color='primary'>
                OTC #{index + 1}
              </Typography>
            </Grid>
            <Grid xs={12}>
              <div>
                <Grid container key={index}>
                  <LabelValuePair
                    label='Item Name'
                    value={
                      find(otcItems, {
                        catalogId: otc.otcCatalogId,
                      }).name
                    }
                  />
                  <LabelValuePair label='Quantity' value={otc.quantity} />
                  <LabelValuePair
                    label='description'
                    value={
                      find(otcItems, {
                        catalogId: otc.otcCatalogId,
                      }).description
                    }
                  />
                  <LabelValuePair label='Instructions' value={otc.instructions} />
                </Grid>
              </div>
            </Grid>
          </Grid>
        ))}

        <Divider sx={{ mt: 3, mb: 1 }} />
      </Box>

      <CheckboxField
        id='Prescriptions-ReviewForm-acknowledge-checkbox'
        name='acknowledge'
        label='I acknowledge that the patient information and prescription is correct.'
        required
      />

      <CheckboxField
        id='Prescriptions-ReviewForm-acknowledge-checkbox'
        name='acknowledge2'
        label={
          <>
            <Box sx={{ my: 1 }}>
              I, {firstName} {lastName}, confirm that I am the individual named in this document and
              I am the author of all entries made herein. I acknowledge that all patient information
              and prescription details are accurate and complete to the best of my knowledge. I
              understand and agree that this electronic signature is the equivalent of my
              handwritten signature.
            </Box>

            <span>Date: {format(new Date(), 'MM/dd/yyyy hh:mm aaa')}</span>
          </>
        }
        required
      />
    </Box>
  );
};

export const steps = [
  { stepLabel: 'Source', Form: <SourceForm /> },
  { stepLabel: 'Patient', Form: <PatientForm /> },
  { stepLabel: 'Medication', Form: <MedicationForm /> },
  { stepLabel: 'Shipping', Form: <ShippingForm /> },
  { stepLabel: 'Review', Form: <ReviewForm /> },
];

const AddressValidationDialog = ({ open, onValidate, onClose }) => {
  const form = useForm();
  const dispatch = useDispatch();
  const formState = useFormState();
  const { setCurrentStep } = useNewOrderForm();
  const [validating, setValidating] = useState(true);
  const [shippingAddressValidation, setShippingAddressValidation] = useState(null);
  const [isInvalidAddress, setInValidAddress] = useState(false);

  const handleAddressConfirmation = () => {
    const selectedAddress =
      formState.values.selectedShippingAddress &&
      JSON.parse(formState.values.selectedShippingAddress);

    form.change(
      'shippingInput',
      selectedAddress && {
        addressLine1: selectedAddress.line1,
        addressLine2: selectedAddress.line2,
        addressCity: selectedAddress.city,
        addressState: selectedAddress.state,
        addressZip: selectedAddress.zip,
        isManuallyVerified: true,
      }
    );
    setShippingAddressValidation(null);
    onValidate();
    onClose();
  };

  const throttledValidateApi = useMemo(
    () =>
      throttle(
        ({ address }) => {
          return validateAddressApi({ input: address });
        },
        500,
        { leading: true, trailing: false }
      ),
    []
  );

  useEffect(() => {
    const { shippingInput, shippingMethod } = form.getState().values;

    if (open && shippingMethod !== 'new') {
      onValidate();
      onClose();
    }

    if (
      open &&
      JSON.stringify(form.getState().values.shippingInput) ===
        form.getState().values.selectedShippingAddress
    ) {
      onValidate();
      onClose();
    }

    if (
      open &&
      !shippingAddressValidation &&
      JSON.stringify(form.getState().values.shippingInput) !==
        form.getState().values.selectedShippingAddress
    ) {
      form.change('selectedShippingAddress', null);

      if (
        shippingInput?.addressLine1 &&
        shippingInput.addressCity &&
        shippingInput.addressState &&
        shippingInput.addressZip
      ) {
        setValidating(true);
        throttledValidateApi({
          address: {
            line1: shippingInput.addressLine1,
            line2: shippingInput.addressLine2,
            city: shippingInput.addressCity,
            state: shippingInput.addressState,
            zip: shippingInput.addressZip,
          },
        })
          .then((addresses) => {
            if (
              addresses.length === 1 &&
              toLower(addresses[0].line1) === toLower(shippingInput.addressLine1) &&
              toLower(addresses[0].line2) === toLower(shippingInput.addressLine2) &&
              toLower(addresses[0].city) === toLower(shippingInput.addressCity) &&
              toLower(addresses[0].state) === toLower(shippingInput.addressState) &&
              toLower(addresses[0].zip) === toLower(shippingInput.addressZip)
            ) {
              form.change('selectedShippingAddress', JSON.stringify(first(addresses)));
              onValidate();
              onClose();
            } else {
              setShippingAddressValidation(addresses);
            }
          })
          .catch(() => {
            form.change(
              'shippingInput',
              omit(form.getState().values.shippingInput, 'addressState')
            ); // this is a hack to trick the input is not the same as selected, so it doesn't close the dialog on select
            setInValidAddress(true);
            setShippingAddressValidation([
              {
                line1: shippingInput.addressLine1,
                line2: shippingInput.addressLine2,
                city: shippingInput.addressCity,
                state: shippingInput.addressState,
                zip: shippingInput.addressZip,
              },
            ]);
          })
          .finally(() => setValidating(false));
      } else {
        onClose();
      }
    }

    return () => {
      if (!open) {
        setShippingAddressValidation(null);
      }
    };
  }, [
    dispatch,
    form,
    onClose,
    onValidate,
    open,
    setCurrentStep,
    shippingAddressValidation,
    throttledValidateApi,
  ]);

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} maxWidth='sm' fullWidth>
      <DialogTitle>{validating ? 'Validating Address' : 'Confirm Address'}</DialogTitle>

      <DialogContent>
        {isInvalidAddress && !validating && (
          <Typography sx={{ mb: 3 }}>
            We were unable to verify the entered address. Please review the address carefully and
            confirm that you have verified its accuracy before proceeding.
          </Typography>
        )}

        {isNil(shippingAddressValidation) && (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}
          >
            <CircularProgress size={100} />
            <Box sx={visuallyHidden}>
              <CheckboxField
                required
                id='validating'
                name='validating'
                label='Validating Address'
              />
            </Box>
          </Box>
        )}

        {!isNil(shippingAddressValidation) && (
          <RadioGroupField
            name='selectedShippingAddress'
            id='FormValidationsChecker-selectedShippingAddress-radioGroup'
            label='Please confirm the shipping address'
            required
          >
            {map(shippingAddressValidation, (address, index) => (
              <FormControlLabel
                value={JSON.stringify(address)}
                key={index}
                control={<Radio />}
                label={`${address.line1}, ${address.line2 ? `${address.line2}, ` : ''}${
                  address.city
                }, ${address.state} ${address.zip}`}
              />
            ))}
          </RadioGroupField>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleAddressConfirmation}
          disabled={!!formState.errors?.selectedShippingAddress || validating}
          variant='contained'
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddressValidationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
};

const NewOrderStepper = () => {
  const form = useForm();
  const finalFormState = useFormState();
  const { currentStep, setCurrentStep, formState, updateFormState } = useNewOrderForm();
  const currentStepRef = useRef(currentStep);
  const [showValidationDialog, setShowShowValidationDialog] = useState(false);
  const currentPartnerRef = useRef();
  const currentPracticeRef = useRef();

  const closeAddressValidationDialog = () => setShowShowValidationDialog(false);

  const handleOnValidate = () => {
    updateFormState({ step: 3, valid: true });
    currentStepRef.current = currentStep;
  };

  const handleStep = (step) => () => setCurrentStep(step);

  const isStepDisabled = (step) => {
    if (step === 0) {
      return false;
    }

    if (isEmpty(formState)) {
      return true;
    }

    return some(
      reduce(
        formState,
        (results, valid, formStep) => {
          if (parseInt(formStep, 10) >= step) {
            return results;
          }

          return [...results, valid];
        },
        []
      ),
      (v) => v === false
    );
  };

  useEffect(() => {
    if (currentStepRef.current === 3 && currentStep !== 3) {
      setShowShowValidationDialog(true);
    } else {
      currentStepRef.current = currentStep;
    }
  }, [currentStep]);

  useEffect(() => {
    if (
      currentPartnerRef.current !== finalFormState.values.partnerId ||
      currentPracticeRef.current !== finalFormState.values.practiceId
    ) {
      form.change('patientInput.patientId', undefined);
      form.change('patientInput.externalPatientId', undefined);
      form.change('patientData', undefined);
      form.change('patientAddress', undefined);

      updateFormState({ step: currentStep + 1, valid: false });

      currentPartnerRef.current = finalFormState.values.partnerId;
      currentPracticeRef.current = finalFormState.values.practiceId;
    }
  }, [
    currentStep,
    form,
    finalFormState.values.partnerId,
    finalFormState.values.practiceId,
    updateFormState,
  ]);

  return (
    <>
      <AddressValidationDialog
        onValidate={handleOnValidate}
        open={showValidationDialog}
        onClose={closeAddressValidationDialog}
      />
      <Stepper activeStep={currentStep} orientation='vertical' nonLinear sx={{ position: 'fixed' }}>
        {map(steps, (step, index) => (
          <Step key={index} completed={formState[index]}>
            <StepButton onClick={handleStep(index)} disabled={isStepDisabled(index)}>
              {step.stepLabel}
            </StepButton>
          </Step>
        ))}
      </Stepper>
    </>
  );
};

const initialValues = {
  medicalHistoryInput: {
    allergies: [],
    conditions: [],
    medications: [],
  },
  prescriptionInput: [],
  otcInput: [],
};

const FormActions = () => {
  const reactFinalFormState = useFormState();
  const { currentStep, setCurrentStep, formState } = useNewOrderForm();

  const handlePrevious = () => setCurrentStep(currentStep - 1);

  const partners = useSelector(({ partner, auth }) => partner[auth.currentTenantId]);

  const selectedPartner = find(partners, { partnerUuid: reactFinalFormState?.values?.partnerId });

  const handleNext = () => setCurrentStep(currentStep + 1);

  return (
    <Grid container direction='row-reverse' sx={{ my: 2 }}>
      {currentStep === steps.length - 1 && (
        <LoadingButton
          variant='contained'
          sx={{ width: 100 }}
          type='submit'
          disabled={!formState[currentStep]}
          loading={reactFinalFormState.submitting}
        >
          Submit
        </LoadingButton>
      )}

      {currentStep < steps.length - 1 && (
        <Tooltip
          title={
            selectedPartner && !selectedPartner.enabled
              ? 'Order submission is restricted. The selected partner is disabled in the system.'
              : ''
          }
        >
          <span>
            <Button
              variant='contained'
              sx={{ width: 100 }}
              onClick={handleNext}
              disabled={!formState[currentStep] || !selectedPartner?.enabled}
            >
              Next
            </Button>
          </span>
        </Tooltip>
      )}

      {currentStep > 0 && (
        <Button
          variant='outlined'
          onClick={handlePrevious}
          disabled={reactFinalFormState.submitting}
          sx={{ mr: 1 }}
        >
          Previous
        </Button>
      )}
    </Grid>
  );
};

const CreateOrderForm = () => {
  const dispatch = useDispatch();
  const { currentStep, setCurrentStep, updateFormState } = useNewOrderForm();
  const [currentForm, setCurrent] = useState(Date.now());
  const [orderReasons, setOrderReasons] = useState([]);
  const [orderResponseType, setOrderResponseType] = useState();
  // POM-6279: how ROUTING_RULES routed the order (relayed / held for review), null when created.
  const [orderRouting, setOrderRouting] = useState({ routingOutcome: null, lineRouting: null });
  const [prescriptionInputs, setPrescriptionInputs] = useState([]);
  const { viewNonRoutableOrders } = useRoles();

  const resetForm = () => {
    setCurrentStep(0);
    setCurrent(Date.now());
    forEach(steps, (_, index) => {
      updateFormState({ step: index, valid: false });
    });
  };

  const handleCreateOrder = (formData) => {
    setPrescriptionInputs(
      map(formData.prescriptionInput, (prescriptionInput) => ({
        drugName: prescriptionInput.drugName,
        strength: prescriptionInput.strength,
      }))
    );

    const shippingInput = {
      patient: {
        addressLine1: formData.patientAddress.line1,
        addressLine2: formData.patientAddress.line2,
        addressCity: formData.patientAddress.city,
        addressState: formData.patientAddress.state,
        addressZip: formData.patientAddress.zip,
      },
      practice: {
        addressLine1: formData.practiceDetails.line1,
        addressLine2: formData.practiceDetails.line2,
        addressCity: formData.practiceDetails.city,
        addressState: formData.practiceDetails.state,
        addressZip: formData.practiceDetails.zip,
      },
      new: formData.shippingInput,
    };

    const order = {
      mpi: formData.patientData.mpi,
      partnerId: formData.partnerId,
      practiceId: formData.practiceId,
      partnerAccount: formData.partnerAccountId,
      isShipToPractice: formData.shippingMethod === 'practice',
      shippingOption: formData.shippingOption,
      otcInput: map(formData.otcInput, (input) => ({
        ...input,
        quantity: parseInt(input.quantity, 10),
      })),
      prescriptionInput: map(formData.prescriptionInput, buildPrescriptionOrderInput),
      referenceId: uuidV4(),
      shippingInput: shippingInput[formData.shippingMethod],
    };

    if (isEmpty(formData.prescriptionInput)) {
      return createPOMOTCOrderApi({ input: omit(order, 'prescriptionInput') })
        .then((success) => {
          if (success) {
            setCurrentStep(steps.length);
            setOrderReasons([]);
            setOrderResponseType('ACCEPTED');
            setOrderRouting({ routingOutcome: null, lineRouting: null });
          }
        })
        .catch((error) => dispatch(handleError({ error })));
    }

    return createPomOrderApi({ order })
      .then((pomOrderResponse) => {
        setOrderReasons(pomOrderResponse.reasons || []);
        setOrderResponseType(pomOrderResponse.responseType);
        setOrderRouting({
          routingOutcome: pomOrderResponse.routingOutcome ?? null,
          lineRouting: pomOrderResponse.lineRouting ?? null,
        });

        setCurrentStep(steps.length);
      })
      .catch((error) => dispatch(handleError({ error })));
  };

  // POM-6279: the Non-Routable Orders queue is named only to those who can open it (SYS_ADMIN).
  const getOrderSubmissionResponseMessage = () =>
    orderSubmissionMessage({
      responseType: orderResponseType,
      routingOutcome: orderRouting.routingOutcome,
      lineRouting: orderRouting.lineRouting,
      canViewNonRoutableOrders: viewNonRoutableOrders,
    });

  const routedLines = routedLineNotes({
    lineRouting: orderRouting.lineRouting,
    prescriptions: prescriptionInputs,
  });

  const getOrderSubmissionResponseIcon = () => {
    const tone = orderSubmissionTone({
      responseType: orderResponseType,
      routingOutcome: orderRouting.routingOutcome,
      lineRouting: orderRouting.lineRouting,
    });

    if (tone === 'error') {
      return <CancelIcon color='error' sx={{ fontSize: '30vh' }} />;
    }

    if (tone === 'success') {
      return <CheckCircleIcon color='success' sx={{ fontSize: '30vh' }} />;
    }

    return (
      <ErrorIcon sx={{ fontSize: '30vh', color: perfectRxTheme.palette.warning.warningColour }} />
    );
  };

  if (currentStep < steps.length) {
    return (
      <Box sx={{ px: 2, pb: 2 }}>
        <Grid container sx={{ mb: 6 }}>
          <Typography component='h1' variant='h4' color='primary' sx={{ position: 'fixed' }}>
            New Order
          </Typography>
        </Grid>

        <Form
          key={currentForm}
          initialValues={initialValues}
          onSubmit={handleCreateOrder}
          render={({ handleSubmit }) => (
            <form noValidate onSubmit={handleSubmit}>
              <FormStateUpdater />
              <OTCItemsListProvider>
                <Grid container>
                  <Grid xs={2}>
                    <NewOrderStepper />
                  </Grid>

                  <Divider orientation='vertical' flexItem />
                  <Grid xs={9.95} sx={{ px: 2, maxWidth: 1400, minHeight: '80vh' }}>
                    <Box>{steps[currentStep].Form}</Box>
                  </Grid>
                </Grid>

                <FormActions />
              </OTCItemsListProvider>
            </form>
          )}
        />
      </Box>
    );
  }

  return (
    <Grid
      container
      direction='column'
      justifyContent='center'
      alignItems='center'
      sx={{ height: '80%' }}
    >
      <Grid>{getOrderSubmissionResponseIcon()}</Grid>
      <Grid>
        <Typography variant='h5' component='h3'>
          {getOrderSubmissionResponseMessage()}
        </Typography>
        {/* POM-6279: which lines left POM (sent on or held), when the lines went different ways. */}
        {routedLines.map(({ key, drug, label }) => (
          <Typography key={key} variant='body1' sx={{ mt: 1 }}>
            <strong>{drug}</strong>: {label}
          </Typography>
        ))}
        <Typography variant='h5' sx={{ mt: 1 }}>
          {prescriptionInputs.map((prescription, index) => {
            const reason = orderReasons[index];
            if (!reason || Object.keys(reason).length === 0) return null;

            const prescriptionKey = `${prescription.drugName}-${prescription.strength}-${index}`;

            return (
              <Grid key={prescriptionKey} sx={{ mb: 2 }}>
                <Grid>
                  {prescriptionDetails.drugName}:{' '}
                  <strong>
                    {prescription.drugName} {prescription.strength}
                  </strong>
                </Grid>

                {Object.entries(reason).map(([key, value]) => (
                  <Grid key={key}>
                    {getRejectionReasonLabel(key)}: {value}
                  </Grid>
                ))}
              </Grid>
            );
          })}
        </Typography>
      </Grid>

      <Grid sx={{ mt: 2 }}>
        <Button
          variant='contained'
          size='large'
          sx={{ width: 300, fontSize: '1.5em' }}
          onClick={resetForm}
        >
          Create another order
        </Button>
      </Grid>
    </Grid>
  );
};

export default CreateOrderForm;
